import {
  runEditWorkspaceWorkflow,
  runMoveWorkspaceToTrashWorkflow,
  runShareWorkspaceWorkflow,
} from "@/action-dialog";
import { useCheckBulkPermissions } from "@/api/relations";
import { useDeleteWorkspace, useUpdateWorkspace } from "@/api/workspaces";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ROUTES } from "@/constants/routes";
import type { Workspace } from "@cohesive/api-client";
import { Pencil, Settings2, Trash2, UserPlus } from "lucide-react";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { HeaderSettingsSlot } from "./HeaderSettingsSlot";
import { HomeClock } from "./HomeClock";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";

/**
 * Workspace title row. Sits where `HomeGreeting` does on the unscoped home and
 * mirrors its markup, so the scoped surface reads as the same page filtered
 * rather than a different one. Deliberately not `WorkspacePageHeader`: that is
 * a full-bleed `max-w-[1600px]` breadcrumb bar, and there are no folders left
 * for a breadcrumb trail to point at.
 */
export function WorkspaceSurfaceHeader({
  workspace,
  workspaceName,
}: {
  workspace?: Workspace;
  workspaceName: string;
}) {
  const navigate = useNavigate();

  const updateWorkspace = useUpdateWorkspace({
    onError: () => toast.error("Failed to update workspace"),
  });
  const deleteWorkspace = useDeleteWorkspace({
    onError: () => toast.error("Failed to delete Workspace"),
  });

  const { data: workspaceDeletePermission } = useCheckBulkPermissions({
    checks: workspace
      ? [
          {
            resource_object_type: "workspace" as const,
            resource_object_id: workspace.id,
            permission: "can_delete" as const,
          },
        ]
      : [],
  });
  const canDeleteWorkspace = workspace
    ? (workspaceDeletePermission?.workspace?.[workspace.id]?.can_delete ??
      false)
    : false;

  const openEditWorkspace = useCallback(() => {
    if (!workspace) return;
    void runEditWorkspaceWorkflow({ workspace, updateWorkspace });
  }, [updateWorkspace, workspace]);

  const openShareWorkspace = useCallback(() => {
    if (!workspace) return;
    void runShareWorkspaceWorkflow({ workspace });
  }, [workspace]);

  const moveWorkspaceToTrash = useCallback(() => {
    if (!workspace) return;
    void runMoveWorkspaceToTrashWorkflow({
      workspace,
      deleteWorkspace,
      onSuccess: () => navigate(ROUTES.HOME),
    });
  }, [deleteWorkspace, navigate, workspace]);

  const icon = workspace?.metadata?.icon;
  /** The private workspace has nothing to edit, share, or archive. */
  const showSettings = !!workspace && !workspace.is_private;

  return (
    // Mirrors HomeGreeting's row: mark + title left, clock right. The workspace
    // icon takes the place of the Cohesive logo, and the title doubles as the
    // workspace switcher.
    <div className="flex w-full items-center justify-between gap-4 select-none">
      <WorkspaceSwitcher
        currentWorkspaceId={workspace?.id}
        workspaceName={workspaceName}
        icon={icon}
      />

      <div className="flex shrink-0 items-center gap-2">
        {/* The control's box is always present — as an invisible twin for the
            private workspace (no settings) and while `workspace` is still
            loading. Rendering it conditionally shifts the clock as you move
            between workspaces, and again once the fetch settles. */}
        {!showSettings && <HeaderSettingsSlot />}
        {showSettings && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Workspace settings"
                title="Workspace settings"
              >
                <Settings2 />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={6} className="w-52">
              {/* Deferred a tick so the menu's exit animation doesn't race the
                  dialog mount — same pattern as the row kebabs. */}
              <DropdownMenuItem
                onClick={() => window.setTimeout(openEditWorkspace, 0)}
              >
                <Pencil />
                Edit workspace
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => window.setTimeout(openShareWorkspace, 0)}
              >
                <UserPlus />
                Add members…
              </DropdownMenuItem>
              {canDeleteWorkspace && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    danger
                    onClick={() => window.setTimeout(moveWorkspaceToTrash, 0)}
                  >
                    <Trash2 />
                    Archive
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <HomeClock />
      </div>
    </div>
  );
}
